import React from 'react';
import { Flag, Stars, Button, Icon } from './primitives.jsx';

// ============================================================================
// Screen: Walkout Animation (Cinematic FUT Reveal)
//
// Three phases:
//   select  — three identical WC26 packs; tap to focus, tap again to commit
//   glow    — chosen pack centers and glows in the country's primary color
//   walkout — original beat machine: tear, card-out, flag burst, settle, CTA
// ============================================================================

export const Walkout = ({ team, onDone }) => {
  const [phase, setPhase] = React.useState('select');
  const [focusedIdx, setFocusedIdx] = React.useState(null);
  const [chosenIdx, setChosenIdx] = React.useState(null);
  const [beat, setBeat] = React.useState(0);
  const canvasRef = React.useRef(null);

  const primary = team.colors?.[0] || '#2F4FFC';
  const secondary = team.colors?.[1] || primary;
  const accent = team.colors?.[2] || secondary;

  // select → glow → walkout
  React.useEffect(() => {
    if (chosenIdx === null) return;
    const t1 = setTimeout(() => setPhase('glow'), 50);
    const t2 = setTimeout(() => setPhase('walkout'), 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [chosenIdx]);

  // Walkout beats (only run once phase enters 'walkout')
  React.useEffect(() => {
    if (phase !== 'walkout') return;
    const timers = [
      setTimeout(() => setBeat(1), 50),
      setTimeout(() => setBeat(2), 900),
      setTimeout(() => setBeat(3), 1600),
      setTimeout(() => setBeat(4), 3100),
      setTimeout(() => setBeat(5), 4300),
      setTimeout(() => setBeat(6), 5100),
    ];
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  // Confetti canvas
  React.useEffect(() => {
    if (beat < 4) return;
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const setSize = () => {
      cvs.width = window.innerWidth * dpr;
      cvs.height = window.innerHeight * dpr;
      cvs.style.width = window.innerWidth + 'px';
      cvs.style.height = window.innerHeight + 'px';
    };
    setSize();
    const onResize = () => setSize();
    window.addEventListener('resize', onResize);

    const cw = cvs.width, ch = cvs.height;
    const colours = team.colors || ['#2F4FFC', '#10154F'];
    const cx = cw / 2, cy = ch / 2;

    const chunks = Array.from({ length: 110 }, () => ({
      x: cx, y: cy,
      vx: (Math.random() - 0.5) * 28 * dpr,
      vy: (Math.random() - 1.1) * 22 * dpr,
      g: 0.32 * dpr,
      life: 1, decay: 0.0045 + Math.random() * 0.003,
      w: (4 + Math.random() * 8) * dpr,
      h: (8 + Math.random() * 14) * dpr,
      color: colours[Math.floor(Math.random() * colours.length)],
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.32,
    }));

    const dots = Array.from({ length: 80 }, () => ({
      x: cx, y: cy,
      vx: (Math.random() - 0.5) * 38 * dpr,
      vy: (Math.random() - 0.5) * 38 * dpr,
      life: 1, decay: 0.012 + Math.random() * 0.01,
      r: (2 + Math.random() * 3) * dpr,
      color: colours[Math.floor(Math.random() * colours.length)],
    }));

    let raf;
    const tick = () => {
      ctx.clearRect(0, 0, cw, ch);
      chunks.forEach(p => {
        if (p.life <= 0) return;
        p.x += p.vx; p.y += p.vy; p.vy += p.g; p.vx *= 0.99;
        p.life -= p.decay; p.rot += p.vr;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w/2, -p.h/2, p.w, p.h);
        ctx.restore();
      });
      dots.forEach(d => {
        if (d.life <= 0) return;
        d.x += d.vx; d.y += d.vy; d.vx *= 0.96; d.vy *= 0.96;
        d.life -= d.decay;
        ctx.save();
        ctx.globalAlpha = Math.max(0, d.life);
        ctx.fillStyle = d.color;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, [beat, team.colors]);

  const handlePackTap = (idx) => {
    if (chosenIdx !== null) return;
    if (focusedIdx === idx) {
      setChosenIdx(idx);
    } else {
      setFocusedIdx(idx);
    }
  };

  return (
    <section style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: phase === 'select' ? 'var(--ink)' : (beat < 2 ? 'var(--blue)' : 'var(--ink)'),
      transition: 'background 800ms var(--ease-cinema)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    }}>
      <style>{`
        @keyframes packet-wobble {
          0%, 100% { transform: rotate(-1.2deg); }
          50%      { transform: rotate(1.2deg); }
        }
        @keyframes tear-off {
          0%   { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(-180px) rotate(-22deg); opacity: 0; }
        }
        @keyframes card-out {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-220px); }
        }
        @keyframes burst-flash {
          0%   { opacity: 0; transform: scale(0.4); }
          30%  { opacity: 1; transform: scale(1.1); }
          70%  { opacity: 1; transform: scale(1.0); }
          100% { opacity: 0; transform: scale(0.95); }
        }
        @keyframes shockwave {
          0%   { transform: scale(0); opacity: 0.8; }
          100% { transform: scale(8); opacity: 0; }
        }
        @keyframes settle {
          0%   { transform: translateY(-220px) scale(1.1); }
          100% { transform: translateY(0) scale(1); }
        }
        @keyframes pulse-ring {
          0%, 100% { box-shadow: 0 0 0 2px ${primary}, 0 32px 80px rgba(0,0,0,0.5); }
          50%      { box-shadow: 0 0 0 4px ${primary}, 0 32px 80px rgba(0,0,0,0.5); }
        }
        @keyframes glow-rise {
          0%   { box-shadow: 0 32px 80px rgba(0,0,0,0.5), 0 0 0 0 ${primary}40; }
          100% { box-shadow: 0 32px 80px rgba(0,0,0,0.5), 0 0 120px 16px ${primary}; }
        }
        @keyframes pack-center {
          0%   { transform: translateX(var(--from-x, 0)) scale(0.86); }
          100% { transform: translateX(0) scale(1); }
        }
        .label-on-ink {
          font-family: var(--font-display);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-size: 14px;
          color: rgba(255,255,255,0.7);
        }
      `}</style>

      {/* Skip */}
      <button onClick={onDone} style={{
        position: 'absolute', top: 24, right: 24, zIndex: 30,
        background: 'transparent', border: '1px solid rgba(255,255,255,0.3)',
        color: 'rgba(255,255,255,0.9)', borderRadius: 999, padding: '8px 16px',
        fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.08em',
        textTransform: 'uppercase', cursor: 'pointer',
      }}>SKIP ›</button>

      {/* Caption */}
      {phase === 'select' && (
        <div style={{
          position: 'absolute', top: 80, left: '50%', transform: 'translateX(-50%)',
          textAlign: 'center', zIndex: 5,
        }}>
          <div className="label-on-ink">CHOOSE YOUR PACK</div>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 12, color: 'rgba(255,255,255,0.5)',
            letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: 8,
          }}>
            {focusedIdx === null ? 'TAP A PACK' : 'TAP AGAIN TO OPEN'}
          </div>
        </div>
      )}

      {/* PHASE: SELECT — three identical WC packs */}
      {phase === 'select' && (
        <PackSelectRow
          focusedIdx={focusedIdx}
          chosenIdx={chosenIdx}
          onTap={handlePackTap}
        />
      )}

      {/* PHASE: GLOW — single centered pack, country-color aura */}
      {phase === 'glow' && (
        <div style={{
          position: 'relative', zIndex: 3,
          animation: 'glow-rise 1.4s var(--ease-out) forwards',
          borderRadius: 8,
        }}>
          <WCPack />
        </div>
      )}

      {/* Flag Burst */}
      {beat === 4 && (
        <>
          <div style={{
            position: 'absolute', left: '50%', top: '50%',
            width: 200, height: 200, marginLeft: -100, marginTop: -100,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${primary} 0%, ${secondary} 40%, transparent 70%)`,
            animation: 'shockwave 1.2s var(--ease-cinema) forwards',
            zIndex: 1,
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 4,
            animation: 'burst-flash 1.2s var(--ease-cinema) forwards',
            filter: `drop-shadow(0 0 48px ${primary})`,
          }}>
            <Flag teamId={team.id} size={Math.min(window.innerWidth * 0.6, 720)} radius={16} />
          </div>
        </>
      )}

      {/* Confetti canvas */}
      {beat >= 4 && (
        <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 6 }} />
      )}

      {/* PHASE: WALKOUT — the chosen pack opening */}
      {phase === 'walkout' && beat < 5 && (
        <div style={{
          position: 'relative', zIndex: 3,
          animation: beat >= 1 ? 'packet-wobble 1.6s var(--ease-in-out) 1' : 'none',
          boxShadow: `0 0 120px 16px ${primary}`,
          borderRadius: 8,
        }}>
          <WCPack torn={beat >= 2} />
          {beat >= 3 && (
            <div style={{
              position: 'absolute', left: '50%', top: 24,
              transform: 'translateX(-50%)',
              animation: 'card-out 1.2s var(--ease-out) forwards',
              transformOrigin: 'top center',
            }}>
              <RevealCard team={team} primary={primary} secondary={secondary} accent={accent} hideFlag={beat < 4} />
            </div>
          )}
        </div>
      )}

      {/* Settled Card */}
      {beat >= 5 && (
        <div style={{
          position: 'relative', zIndex: 8,
          animation: 'settle 0.9s var(--ease-pop) forwards' + (beat >= 6 ? ', pulse-ring 2.4s var(--ease-in-out) 0.9s infinite' : ''),
          borderRadius: 12,
        }}>
          <RevealCard team={team} primary={primary} secondary={secondary} accent={accent} hideFlag={false} />
        </div>
      )}

      {/* Continue */}
      {beat >= 6 && (
        <div style={{ position: 'absolute', bottom: 56, left: '50%', transform: 'translateX(-50%)', zIndex: 20 }}>
          <Button variant="onInk" onClick={onDone} style={{ whiteSpace: 'nowrap' }}>
            TO DASHBOARD <Icon name="arrow-right" size={16} />
          </Button>
        </div>
      )}
    </section>
  );
};

// ----------------------------------------------------------------------------
// Pack selection row: three identical packs, tap-to-focus, tap-again-to-commit
// ----------------------------------------------------------------------------
const PackSelectRow = ({ focusedIdx, chosenIdx, onTap }) => {
  // Responsive: scale packs so all three fit the viewport with a safe margin.
  // Old version hardcoded 0.58 on narrow screens — at 375px viewport (iPhone SE)
  // that produced 3*280*0.58 + 2*8 ≈ 503px of pack row inside an overflow:hidden
  // section, which clipped two of the three packs entirely.
  const [vw, setVw] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  React.useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isNarrow = vw < 640;
  const gap = isNarrow ? 6 : 28;
  // 3 packs × (280px × scale) + 2 × gap ≤ vw − 24 side padding
  const fitScale = (vw - 24 - 2 * gap) / (3 * 280);
  const packScale = Math.max(0.32, Math.min(isNarrow ? 0.58 : 0.78, fitScale));

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap, perspective: 1200, zIndex: 5,
      width: '100%',
    }}>
      {[0, 1, 2].map(i => {
        const isFocused = focusedIdx === i;
        const isOther = focusedIdx !== null && !isFocused;
        const dim = isOther && chosenIdx === null;
        // The button needs to claim REAL layout space at the scaled size,
        // otherwise flex/justify-content positions it as if it were 280×380
        // and the sides clip against overflow:hidden. We size the button
        // here and apply the visual scale to an inner wrapper that still
        // contains the 280×380 native-sized WCPack.
        return (
          <button
            key={i}
            onClick={() => onTap(i)}
            aria-label={`Pack ${i + 1}`}
            style={{
              background: 'transparent', border: 'none', padding: 0,
              cursor: chosenIdx === null ? 'pointer' : 'default',
              width: 280 * packScale,
              height: 380 * packScale,
              position: 'relative',
              transformStyle: 'preserve-3d',
              transform: `translateY(${isFocused ? -20 : 0}px) scale(${isFocused ? 1.18 : 1})`,
              transformOrigin: 'center',
              transition: 'transform 500ms var(--ease-pop), opacity 500ms var(--ease-out), filter 500ms var(--ease-out)',
              opacity: dim ? 0.35 : 1,
              filter: isFocused ? 'drop-shadow(0 24px 48px rgba(0,0,0,0.6))' : 'drop-shadow(0 12px 24px rgba(0,0,0,0.4))',
              zIndex: isFocused ? 2 : 1,
            }}
          >
            <div style={{
              position: 'absolute', top: 0, left: 0,
              width: 280, height: 380,
              transform: `scale(${packScale})`,
              transformOrigin: 'top left',
            }}>
              <WCPack />
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ----------------------------------------------------------------------------
// WCPack: the generic World Cup pack front (identical for every team).
// Country reveal happens AFTER the pack opens — pack itself stays neutral.
// ----------------------------------------------------------------------------
const WCPack = ({ torn = false }) => {
  return (
    <div style={{
      width: 280, height: 380,
      position: 'relative',
      borderRadius: 6,
      background: `
        radial-gradient(120% 80% at 50% 0%, rgba(255,255,255,0.08) 0%, transparent 60%),
        linear-gradient(160deg, #1a1f4a 0%, #0b0f2e 60%, #050816 100%)
      `,
      boxShadow: '0 32px 80px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.12)',
      overflow: 'hidden',
    }}>
      {/* Foil sheen */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(115deg, transparent 30%, rgba(255,215,0,0.08) 45%, rgba(255,215,0,0.2) 50%, rgba(255,215,0,0.08) 55%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Top strip (tear-off zone) */}
      <div style={{
        position: 'absolute', top: -2, left: -2, right: -2, height: 56,
        background: 'linear-gradient(160deg, #FFD700 0%, #C09300 100%)',
        borderTopLeftRadius: 6, borderTopRightRadius: 6,
        borderBottom: '2px dashed rgba(0,0,0,0.35)',
        transformOrigin: 'top center',
        animation: torn ? 'tear-off 700ms var(--ease-cinema) forwards' : 'none',
      }}>
        <div style={{ position: 'absolute', left: -8, top: 18, width: 16, height: 16, borderRadius: 999, background: 'var(--ink)' }} />
        <div style={{ position: 'absolute', right: -8, top: 18, width: 16, height: 16, borderRadius: 999, background: 'var(--ink)' }} />
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-display)', textTransform: 'uppercase',
          letterSpacing: '0.18em', color: 'rgba(0,0,0,0.7)', fontSize: 13,
        }}>TEAR HERE</div>
      </div>

      {/* WC26 wordmark */}
      <div style={{
        position: 'absolute', top: 92, left: 0, right: 0, textAlign: 'center',
        fontFamily: 'var(--font-display)', fontSize: 36, color: '#FFD700',
        textTransform: 'uppercase', letterSpacing: '-0.005em', lineHeight: 1,
        textShadow: '0 2px 12px rgba(255,215,0,0.4)',
      }}>
        WC<span style={{ color: 'rgba(255,255,255,0.95)' }}>26</span>
      </div>
      <div style={{
        position: 'absolute', top: 140, left: 0, right: 0, textAlign: 'center',
        fontFamily: 'var(--font-display)', fontSize: 11, color: 'rgba(255,255,255,0.6)',
        textTransform: 'uppercase', letterSpacing: '0.28em',
      }}>OFFICIAL · SWEEPSTAKE</div>

      {/* Centerpiece: stylized trophy / star mark */}
      <div style={{
        position: 'absolute', top: 178, left: '50%', transform: 'translateX(-50%)',
        width: 100, height: 100, borderRadius: 999,
        background: 'radial-gradient(circle, rgba(255,215,0,0.25) 0%, transparent 70%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 56, lineHeight: 1,
      }}>🏆</div>

      {/* Bottom seal */}
      <div style={{
        position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
        width: 88, height: 88, borderRadius: 999,
        background: 'rgba(255,255,255,0.08)',
        border: '2px solid rgba(255,215,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-display)', fontSize: 12, color: '#FFD700',
        textTransform: 'uppercase', letterSpacing: '0.12em', textAlign: 'center', lineHeight: 1.1,
      }}>
        ONE<br/>OF<br/>48
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// RevealCard: country flag + name + group + strength stars
// ----------------------------------------------------------------------------
const RevealCard = ({ team, primary, secondary, accent, hideFlag }) => {
  return (
    <div style={{
      width: 280, height: 380,
      background: 'var(--paper)',
      borderRadius: 12,
      padding: 0,
      overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
      border: `2px solid ${primary}`,
    }}>
      {/* Top stripe: group letter + stars */}
      <div style={{
        height: 48, padding: '12px 18px',
        background: primary, color: '#fff',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 18,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
          GROUP {team.group}
        </span>
        <Stars value={team.strength} size={14} color="#fff" />
      </div>

      {/* Flag panel */}
      <div style={{
        flex: 1, position: 'relative',
        background: `linear-gradient(180deg, ${secondary} 0%, ${primary} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <div style={{
          opacity: hideFlag ? 0 : 1,
          transform: hideFlag ? 'scale(0.6)' : 'scale(1)',
          transition: 'opacity 400ms var(--ease-out), transform 600ms var(--ease-pop)',
          filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))',
        }}>
          <Flag teamId={team.id} size={180} radius={8} />
        </div>
      </div>

      {/* Country name */}
      <div style={{
        background: 'var(--paper)', color: 'var(--ink)',
        padding: '18px 18px 20px', textAlign: 'center',
        borderTop: `2px solid ${primary}`,
      }}>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 11,
          letterSpacing: '0.22em', textTransform: 'uppercase', color: primary,
        }}>YOUR TEAM</div>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 30,
          letterSpacing: '-0.005em', textTransform: 'uppercase',
          lineHeight: 1.05, marginTop: 4,
        }}>{team.country}</div>
      </div>
    </div>
  );
};
